import React, { useEffect, useRef, useMemo } from 'react';
import * as echarts from 'echarts';
import { useAppContext } from 'providers/AppProvider';
import { tooltipFormatterScoreScatter } from 'helpers/echart-utils';

interface ScoreData {
  scorevalue: number;
  calls: number;
}

interface ScoreVsDurationChartProps {
  data: ScoreData[];
}

const ScoreDistributionChart: React.FC<ScoreVsDurationChartProps> = ({
  data
}) => {
  // eslint-disable-next-line
  const { config: { isDark }, getThemeColor, addAlphaToHexColor } = useAppContext();
  const chartRef = useRef<HTMLDivElement>(null);

  // Convertir los datos en un TypedArray para mejorar el rendimiento
  const memoizedData = useMemo(() => {
    const dataLength = data.length;
    const formattedData = new Float32Array(dataLength * 2);

    data.forEach((item, index) => {
      formattedData[index * 2] = item.calls;
      formattedData[index * 2 + 1] = item.scorevalue;
    });

    return formattedData;
  }, [data]);

  useEffect(() => {
    let myChart: echarts.ECharts | undefined;

    const handleResize = () => {
      if (myChart) {
        myChart.resize(); // Ajustar el tamaño del gráfico cuando cambie el tamaño de la ventana
      }
    };

    // Inicializar el gráfico cuando el componente se monta
    try {
      if (chartRef.current) {
        // Usar WebGL para mejorar el rendimiento
        // eslint-disable-next-line
        myChart = echarts.init(chartRef.current, undefined, { renderer: 'canvas' }); //svg or canvas

        const option: echarts.EChartsOption = {
          title: {
            show: false
          },
          visualMap: {
            type: 'piecewise',
            show: false,
            dimension: 1,
            orient: 'vertical',
            right: 0,
            top: 'center',
            pieces: [
              { min: 8, color: 'rgba(37, 176, 3, 1)' },
              { min: 6, max: 8, color: 'rgba(229, 120, 11, 1)' },
              { max: 6, color: 'rgba(192, 0, 0, 1)' }
            ],
            textStyle: {
              // eslint-disable-next-line
              color: !isDark ? 'rgba(110, 110, 130, 0.9)' : 'rgba(100, 100, 150, 0.7)',
              fontFamily: 'Nunito Sans',
              fontSize: 9
            }
          },
          dataZoom: [
            {
              type: 'inside',
              yAxisIndex: [0, 1],
              start: 0,
              end: 100,
              filterMode: 'filter',
              zoomOnMouseWheel: false,
              moveOnMouseWheel: false,
              moveOnMouseMove: true
            },
            {
              show: true,
              yAxisIndex: [0, 1],
              type: 'slider',
              //left: '25',
              right: '10',
              width: '20',
              top: '23',
              start: 0,
              end: 100,
              fillerColor: addAlphaToHexColor(getThemeColor('primary'), 0.1),
              borderColor: addAlphaToHexColor(getThemeColor('primary'), 0.1),
              handleStyle: {
                color: getThemeColor('primary-light'),
                borderColor: getThemeColor('primary-light')
              },
              textStyle: {
                color: 'rgba(0, 0, 0, 0)',
                fontSize: 10
              },
              brushSelect: false
            }
          ],
          grid: {
            left: '35',
            right: '42',
            top: '23',
            height: '247'
          },
          tooltip: {
            trigger: 'item',
            // eslint-disable-next-line
            formatter: (params: any) => tooltipFormatterScoreScatter(params, 'MMM DD, YYYY'),
            // eslint-disable-next-line
            backgroundColor: !isDark ? 'rgba(240, 240, 240, 0.9)' : 'rgba(49, 55, 74, 0.9)',
            textStyle: {
              color: !isDark ? '#202020' : '#ccc',
              fontFamily: 'Nunito Sans',
              fontSize: 14
            },
            borderWidth: 1,
            // eslint-disable-next-line
            borderColor: !isDark ? 'rgba(49, 55, 74, 0.4)' : 'rgba(140, 140, 180, 0.8)',
            padding: 10
          },
          toolbox: {
            itemSize: 10,
            right: '8',
            showTitle: false,
            show: true,
            iconStyle: {
              // eslint-disable-next-line
              borderColor: !isDark ? 'rgba(160, 160, 190, 0.9)' : 'rgba(100, 100, 150, 0.9)'
            },
            emphasis: {
              iconStyle: { borderColor: getThemeColor('primary') }
            },
            feature: {
              mark: { show: false },
              magicType: { show: false, type: ['line', 'bar'] },
              dataZoom: { show: false },
              dataView: { show: false, readOnly: true },
              restore: { show: false },
              saveAsImage: { show: true },
              brush: { type: ['lineX', 'clear'], show: false }
            }
          },
          xAxis: {
            type: 'value',
            name: 'Number of calls',
            nameLocation: 'middle',
            nameGap: 20,
            axisLine: {
              lineStyle: {
                // eslint-disable-next-line
                color: !isDark ? 'rgba(160, 160, 190, 0.5)' : 'rgba(100, 100, 150, 0.5)'
              }
            },
            axisLabel: {
              fontSize: 8.8,
              // eslint-disable-next-line
              color: !isDark ? 'rgba(110, 110, 130, 0.9)' : 'rgba(130, 130, 140, 0.9)'
            },
            splitLine: {
              lineStyle: {
                // eslint-disable-next-line
                color: !isDark ? 'rgba(160, 160, 190, 0.2)' : 'rgba(100, 100, 150, 0.2)' // Customize grid line color
              }
            },
            nameTextStyle: {
              // eslint-disable-next-line
              color: !isDark ? 'rgba(110, 110, 130, 0.9)' : 'rgba(130, 130, 140, 0.9))'          
            }
          },
          yAxis: {
            type: 'value',
            name: 'Score',
            nameLocation: 'middle',
            nameGap: 20,
            scale: true,
            axisLine: {
              lineStyle: {
                // eslint-disable-next-line
                color: !isDark ? 'rgba(160, 160, 190, 0.5)' : 'rgba(100, 100, 150, 0.5)'
              }
            },
            axisLabel: {
              fontSize: 8.8,
              // eslint-disable-next-line
              color: !isDark ? 'rgba(110, 110, 130, 0.9)' : 'rgba(130, 130, 140, 0.9))'
            },
            splitLine: {
              lineStyle: {
                // eslint-disable-next-line
                color: !isDark ? 'rgba(160, 160, 190, 0.2)' : 'rgba(100, 100, 150, 0.2)' // Customize grid line color
              }
            },
            nameTextStyle: {
              // eslint-disable-next-line
              color: !isDark ? 'rgba(110, 110, 130, 0.9)' : 'rgba(130, 130, 140, 0.9)'          
            }
          },
          animation: false,
          series: [
            {
              name: 'Score',
              type: 'scatter',
              symbolSize: 5,
              data: memoizedData, // Usar TypedArray
              dimensions: ['duration', 'scorevalue'], // Definir las dimensiones
              encode: {
                x: 0, // El primer valor es la duración
                y: 1 // El segundo valor es el score
              },
              large: true,
              itemStyle: {
                // eslint-disable-next-line
                color: !isDark ? 'rgba(192, 0, 0, 1)' : 'rgba(160, 50, 50, 1)'
              }
            }
          ]
        };

        // Configurar la opción en el gráfico
        myChart.setOption(option);

        // Añadir el event listener para redimensionar
        window.addEventListener('resize', handleResize);
      }
    } catch (error) {
      alert('Error initializing chart:' + error);
    }

    // Limpiar el gráfico al desmontar el componente
    return () => {
      window.removeEventListener('resize', handleResize);
      myChart?.dispose();
    };
  }, [data, isDark]);

  return <div ref={chartRef} style={{ width: '100%', height: '300px' }} />;
};

export default ScoreDistributionChart;
